import { useState } from 'react';
import { UserRoles } from '@/enums/enums';
import { getUserOptions } from '@/features/user/user.queries';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { BookOpen, CalendarIcon, GraduationCap, Wallet } from 'lucide-react';

import { AnalyticsParams } from '@/types/analytics.types';
import { Option } from '@/types/types';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { MonthRangePicker } from '@/components/ui/monthrangepicker';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OrganizationFilter } from '@/components/organization-filter';

import StudentsTab from './-studentsTab';
import ContractsTab from './-contractsTab';
import LessonsTab from './-lessonsTab';
import GradesTab from '../(account)/-GradesTab';

const now = new Date();
const DEFAULT_RANGE = {
  start: new Date(now.getFullYear(), 0), // Jan of current year
  end: new Date(now.getFullYear(), now.getMonth()), // Current month
};

export const Route = createFileRoute('/_app/(analytics)/analytics/')({
  component: AnalyticsPage,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);

    if (![UserRoles.Root, UserRoles.Admin].includes(user?.role as UserRoles)) {
      throw redirect({
        to: '/',
      });
    }
  },
});

function AnalyticsPage() {
  const [selectedOrganizations, setSelectedOrganizations] = useState<Option[]>([]);
  const [monthRange, setMonthRange] = useState(DEFAULT_RANGE);
  const [pickerOpen, setPickerOpen] = useState(false);

  const organizationIds =
    selectedOrganizations.length > 0
      ? selectedOrganizations.map((o) => o.value as number)
      : undefined;

  const params: AnalyticsParams = {
    fromMonth: format(monthRange.start, 'yyyy-MM'),
    toMonth: format(monthRange.end, 'yyyy-MM'),
    organizationIds,
  };

  const rangeLabel = `${format(monthRange.start, 'MMM yyyy', { locale: el })} - ${format(monthRange.end, 'MMM yyyy', { locale: el })}`;

  return (
    <div className='flex h-full flex-col'>
      {/* ── Page header ─────────────────────────────────── */}
      <div className='flex flex-row items-center justify-between px-6 py-4'>
        <div className='flex flex-col'>
          <h1 className='text-xl font-bold tracking-tight'>Αναλυτικά Στοιχεία</h1>
          <p className='mt-0.5 text-sm text-muted-foreground'>
            Επισκόπηση δεδομένων και στατιστικών
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <OrganizationFilter
            selected={selectedOrganizations}
            onApply={setSelectedOrganizations}
          />
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant='outline'
                className={cn('w-[260px] justify-start text-left font-normal')}
              >
                <CalendarIcon className='mr-2 h-4 w-4' />
                {rangeLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='end'>
              <MonthRangePicker
                selectedMonthRange={monthRange}
                onMonthRangeSelect={(range) => {
                  setMonthRange(range);
                  setPickerOpen(false);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────── */}
      <Tabs defaultValue='students' className='flex flex-1 flex-col'>
        <div className='border-b px-6'>
          <TabsList className='h-auto gap-4 bg-transparent p-0'>
            <TabsTrigger
              value='students'
              className='gap-1.5 rounded-none border-b-2 border-transparent px-1 pb-3 pt-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none'
            >
              <GraduationCap className='h-4 w-4' />
              Μαθητές
            </TabsTrigger>
            <TabsTrigger
              value='contracts'
              className='gap-1.5 rounded-none border-b-2 border-transparent px-1 pb-3 pt-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none'
            >
              <Wallet className='h-4 w-4' />
              Συμβόλαια & Έσοδα
            </TabsTrigger>
            <TabsTrigger
              value='lessons'
              className='gap-1.5 rounded-none border-b-2 border-transparent px-1 pb-3 pt-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none'
            >
              <BookOpen className='h-4 w-4' />
              Μαθήματα
            </TabsTrigger>
            <TabsTrigger
              value='grades'
              className='gap-1.5 rounded-none border-b-2 border-transparent px-1 pb-3 pt-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none'
            >
              <BookOpen className='h-4 w-4' />
              Βαθμολογίες
            </TabsTrigger>
          </TabsList>
        </div>

        <div className='flex-1 overflow-y-auto'>
          <TabsContent value='students' className='mt-0 h-full'>
            <StudentsTab params={params} />
          </TabsContent>
          <TabsContent value='contracts' className='mt-0 h-full'>
            <ContractsTab params={params} />
          </TabsContent>
            <TabsContent value='lessons' className='mt-0 h-full'>
              <LessonsTab params={params} />
            </TabsContent>
            <TabsContent value='grades' className='mt-0 h-full'>
              <GradesTab
                hideRangePicker
                fromMonth={format(monthRange.start, 'yyyy-MM')}
                toMonth={format(monthRange.end, 'yyyy-MM')}
              />
            </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
